<script setup lang="ts">
import { computed, ref } from "vue";
import { useFormat } from "~/composables/use-format";

interface OrderEventLite {
  id?: string;
  name?: string;
}

interface ReservationProductLite {
  id?: string;
  productName?: string;
  productPrice?: number;
  quantity?: number;
  currentQuantity?: number;
  totalPrice?: number;
  currentTotalPrice?: number;
}

interface ReservationLite {
  reservationProducts?: ReservationProductLite[] | null;
}

interface OrderDetail {
  id: string;
  code: string;
  status: string;
  paymentStatus?: string;
  currency: string;

  buyerName?: string;
  buyerEmail?: string;
  buyerPhone?: string;
  buyerAddress?: string;
  buyerCountry?: string;

  event?: OrderEventLite | null;
  reservation?: ReservationLite | null;

  totalPrice: number;
  currentTotalPrice?: number;
  discount?: number;
  shippingFee?: number;
  paidAmount?: number;
  refundedAmount?: number;
  dueAmount?: number;

  createTime: number;
  confirmTime?: number;
  cancelTime?: number;
}

const { t } = useI18n();
const { d, c } = useFormat();

const open = ref(false);
const order = ref<OrderDetail | null>(null);

function openModal(row: OrderDetail) {
  order.value = row;
  open.value = true;
}

function close() {
  open.value = false;
}

const lineItems = computed(() => {
  const products = order.value?.reservation?.reservationProducts || [];
  return products.map((p) => ({
    productName: p.productName || "—",
    quantity: p.currentQuantity ?? p.quantity ?? 0,
    unitPrice: p.productPrice ?? 0,
    totalPrice: p.currentTotalPrice ?? p.totalPrice ?? 0,
  }));
});

function statusIntent(status: string) {
  switch (status) {
    case "confirmed":
      return "positive";
    case "cancelled":
      return "negative";
    case "pending":
      return "warning";
    case "open":
      return "informative";
    default:
      return "neutral";
  }
}

function paymentIntent(status: string) {
  switch (status) {
    case "paid":
      return "positive";
    case "partiallyPaid":
      return "warning";
    case "refunded":
      return "neutral";
    case "failed":
      return "negative";
    default:
      return "neutral";
  }
}

function statusLabel(status: string) {
  if (!status) return "—";
  const key = `order.status${status.charAt(0).toUpperCase() + status.slice(1)}`;
  return t(key);
}

function paymentStatusLabel(status: string) {
  if (!status) return "—";
  const key = `order.paymentStatus${status.charAt(0).toUpperCase() + status.slice(1)}`;
  return t(key);
}

defineExpose({
  open: openModal,
  close,
});
</script>

<template>
  <Modal
    :open="open"
    :title="order ? t('order.modal.title', { code: order.code }) : ''"
    size="lg"
    @update:open="(v: boolean) => (open = v)"
    @close="close"
  >
    <div v-if="order" class="order-detail flex flex-col gap-large">
      <div class="flex items-center justify-between flex-wrap gap-small">
        <div class="flex items-center gap-2xsmall flex-wrap">
          <Badge :intent="statusIntent(order.status)" emphasis="medium" size="small">
            {{ statusLabel(order.status) }}
          </Badge>
          <Badge
            v-if="order.paymentStatus"
            :intent="paymentIntent(order.paymentStatus)"
            emphasis="medium"
            size="small"
          >
            {{ paymentStatusLabel(order.paymentStatus) }}
          </Badge>
        </div>
        <span class="text-body-small-tertiary content-neutral-medium">
          {{ d(order.createTime, 'datetime') }}
        </span>
      </div>

      <Divider />

      <section class="flex flex-col gap-xsmall">
        <h3 class="text-body-secondary content-neutral-strong">
          {{ t("order.modal.sectionCustomer") }}
        </h3>
        <div class="flex flex-col gap-2xsmall">
          <span class="font-semibold">{{ order.buyerName || "—" }}</span>
          <span class="text-body-small-tertiary content-neutral-medium">
            {{ order.buyerEmail }}<template v-if="order.buyerPhone"> · {{ order.buyerPhone }}</template>
          </span>
          <span
            v-if="order.buyerAddress || order.buyerCountry"
            class="text-body-small-tertiary content-neutral-medium"
          >
            {{ [order.buyerAddress, order.buyerCountry].filter(Boolean).join(", ") }}
          </span>
        </div>
      </section>

      <template v-if="order.event && order.event.name">
        <Divider />
        <section class="flex flex-col gap-xsmall">
          <h3 class="text-body-secondary content-neutral-strong">
            {{ t("order.modal.sectionEvent") }}
          </h3>
          <span class="font-semibold">{{ order.event.name }}</span>
        </section>
      </template>

      <Divider />

      <section class="flex flex-col gap-xsmall">
        <h3 class="text-body-secondary content-neutral-strong">
          {{ t("order.modal.sectionLineItems") }}
        </h3>
        <DataTable
          v-if="lineItems.length"
          :columns="[
            { key: 'product', label: t('order.modal.lineColumnProduct'), class: 'md:min-w-[240px]' },
            { key: 'qty', label: t('order.modal.lineColumnQty'), align: 'right', class: 'min-w-[80px]' },
            { key: 'unit', label: t('order.modal.lineColumnUnit'), align: 'right', class: 'min-w-[112px]' },
            { key: 'total', label: t('order.modal.lineColumnTotal'), align: 'right', class: 'min-w-[112px]' },
          ]"
          :items="lineItems"
        >
          <template #cell-product="{ row }">{{ row.productName }}</template>
          <template #cell-qty="{ row }">{{ row.quantity }}</template>
          <template #cell-unit="{ row }">{{ c(row.unitPrice, order.currency) }}</template>
          <template #cell-total="{ row }">{{ c(row.totalPrice, order.currency) }}</template>
        </DataTable>
        <div v-else class="text-body-small-tertiary content-neutral-medium">
          {{ t("order.modal.noLineItems") }}
        </div>
      </section>

      <Divider />

      <section class="flex flex-col gap-2xsmall">
        <h3 class="text-body-secondary content-neutral-strong">
          {{ t("order.modal.sectionTotals") }}
        </h3>
        <div class="order-detail__totals">
          <span>{{ t("order.modal.labelSubtotal") }}</span>
          <span>{{ c(order.totalPrice, order.currency) }}</span>
        </div>
        <div class="order-detail__totals">
          <span>{{ t("order.modal.labelDiscount") }}</span>
          <span>−{{ c(order.discount || 0, order.currency) }}</span>
        </div>
        <div class="order-detail__totals">
          <span>{{ t("order.modal.labelShipping") }}</span>
          <span>{{ c(order.shippingFee || 0, order.currency) }}</span>
        </div>
        <div class="order-detail__totals is-strong">
          <span>{{ t("order.modal.labelTotal") }}</span>
          <span>{{ c(order.currentTotalPrice ?? order.totalPrice, order.currency) }}</span>
        </div>
        <div class="order-detail__totals">
          <span>{{ t("order.modal.labelPaid") }}</span>
          <span>{{ c(order.paidAmount || 0, order.currency) }}</span>
        </div>
        <div class="order-detail__totals">
          <span>{{ t("order.modal.labelRefunded") }}</span>
          <span>{{ c(order.refundedAmount || 0, order.currency) }}</span>
        </div>
        <div class="order-detail__totals">
          <span>{{ t("order.modal.labelDue") }}</span>
          <span>{{ c(order.dueAmount || 0, order.currency) }}</span>
        </div>
      </section>

      <Divider />

      <section class="flex flex-col gap-2xsmall">
        <h3 class="text-body-secondary content-neutral-strong">
          {{ t("order.modal.sectionTimestamps") }}
        </h3>
        <div class="order-detail__totals">
          <span>{{ t("order.modal.labelCreated") }}</span>
          <span>{{ d(order.createTime, 'datetime') }}</span>
        </div>
        <div v-if="order.confirmTime" class="order-detail__totals">
          <span>{{ t("order.modal.labelConfirmed") }}</span>
          <span>{{ d(order.confirmTime, 'datetime') }}</span>
        </div>
        <div v-if="order.cancelTime" class="order-detail__totals">
          <span>{{ t("order.modal.labelCancelled") }}</span>
          <span>{{ d(order.cancelTime, 'datetime') }}</span>
        </div>
      </section>
    </div>

    <template #actions>
      <Button variant="tertiary" size="small" @click="close">
        {{ t("action.close") }}
      </Button>
    </template>
  </Modal>
</template>

<style scoped lang="scss">
.order-detail__totals {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: token("gap-medium");

  &.is-strong {
    font-weight: 600;
    color: token("color-content-neutral-strong");
  }

  > span:first-child {
    color: token("color-content-neutral-medium");
  }
}
</style>
